import { AddLinkModalComponent } from "./modal-wizard/add-link-modal.component";
import { TableFilterService } from "@nms-ng2/app/shared/services/table-filter/table-filter-service";
import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
    ColumnConfig,
    FilterColumnParameters,
    FilterOperator,
    NmsTableComponent,
    NmsTableValidatorService,
    SortConfig,
    RowsValidateModel,
    PaginatedResponse,
    TableDataRuleType,
    RequestConfig
} from "@nms-angular-toolkit/nms-table";
import { RefreshIntervalService } from "@nms-ng2/app/shared/services/refresh-interval/refresh-interval.service";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_TRANSLATE,
    NMS_FEATURES,
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Observable, Subscription } from "rxjs";
import { LinkInfo } from "./links.model";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import { LinkActionModel } from "@nms-angular-toolkit/nms-link";
import { NmsToastrLinkService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr-link.service";
import { LinksShortcutActionService } from "../utils/links-shortcut-action.service";
import { LinksTableService } from "./services/links-table.service";
import { LinkService } from "./services/link.service";

/**
 * Componente de exibição dos links.
 */
@Component({
    selector: "links",
    templateUrl: "./links.component.html",
    styleUrls: ["./links.component.scss"]
})
export class LinksComponent implements AfterViewInit, OnDestroy, OnInit {
    private readonly baseUrl: string = "/topology/link/search-all";

    @ViewChild(NmsTableComponent)
    nmsTableComponent: NmsTableComponent;

    @Input()
    isOpenInModal: boolean;
    @Input()
    tableId: string = "nms-links";
    autoUpdateInterval: number;
    linksProvider;
    nmsTableChangedSubscription: Subscription;
    nmsTableRulesRemovedSubscription: Subscription;
    @Input()
    requestConfig: RequestConfig = {
        serverPagination: {
            baseUrl: this.baseUrl
        }
    };

    operators: Array<FilterOperator> = [];
    initialSort: SortConfig[] = [];
    columns: ColumnConfig[] = [];
    linkActions: LinkActionModel[] = [];

    loggedUser: string;

    columnsSettingsLabels = {};

    constructor(private readonly refreshIntervalService: RefreshIntervalService,
            @Inject(ANGULARJS_TRANSLATE) private translate: any,
            @Inject(ModalFactory) private readonly modalFactory: ModalFactory, @Inject(NMS_FEATURES) private nmsFeatures: any,
            private tableFilterService: TableFilterService, private nmsTableValidatorService: NmsTableValidatorService,
            private linkService: LinkService, private nmsToastrLinkService: NmsToastrLinkService,
            private readonly linksShortcutActionService: LinksShortcutActionService,
            @Inject(ANGULARJS_ROOTSCOPE) private readonly rootScope: any,
            private readonly linksTableService: LinksTableService) {

        this.loggedUser = this.rootScope.loggedUser;
        this.linksProvider = this.retrieveTableLinks;
        this.refreshIntervalService.getRefreshInterval((interval) => {
            this.autoUpdateInterval = interval;
        });
        this.linkService.reloadListEmmiter.subscribe(() => this.updateTableDataAndNotify());

        this.operators = [
            {
                value: "AND",
                label: this.translate.instant("nms-table.filter.and"),
                selected: true
            },
            {
                value: "OR",
                label: this.translate.instant("nms-table.filter.or"),
                selected: false
            }
        ];

        this.initialSort = [
            {
                column: "name",
                direction: "asc",
                label: this.translate.instant("table.links.name"),
            }
        ];

        this.columnsSettingsLabels = {
            iconTooltip: this.translate.instant("nms.table.organize.columns.tooltip"),
            noColumnFound: this.translate.instant("nms.table.organize.columns.search.not.found"),
            showHideColumns: this.translate.instant("nms.table.show.hide.columns.columns"),
            showHideColumnsTooltip: this.translate.instant("nms.table.show.hide.columns.tooltip"),
            searchColumnsPlaceholder: this.translate.instant("nms.table.organize.columns.search"),
            restoreOrdering: this.translate.instant("nms.table.organize.restore.ordering")
        };

    }

    isTableLoaded() {
        return this.nmsTableComponent !== undefined;
    }

    ngAfterViewInit(): void {
        this.retrieveTableLinks = this.retrieveTableLinks.bind(this);
        this.subscribeTableChange();
        this.subscribeTableRulesRemovedEvent();
    }

    ngOnInit(): void {
        this.setColumns();
        if (!this.isOpenInModal) {
            this.linkActions = [
                {
                    id: "add-link",
                    translateKey: this.translate.instant("table.links.add"),
                    action: () => this.addLink()
                },
                {
                    id: "edit-link",
                    translateKey: this.translate.instant("table.links.edit"),
                    action: () => {
                        const multipleRows = false;
                        this.handleBeforeEdit(multipleRows);
                    }
                },
                {
                    id: "remove-link",
                    translateKey: this.translate.instant("table.links.remove"),
                    action: () => {
                        this.handleBeforeRemove();
                    }
                }
            ];
        }
    }

    retrieveTableLinks(): Observable<Object> {
        return this.nmsTableComponent.getProvider();
    }

    subscribeTableChange() {
        this.nmsTableChangedSubscription = this.nmsTableComponent?.contentChangeEvent.subscribe((links) => {
            Promise.all(links).then(() => {
                this.nmsTableComponent.applyLoading.next(false);
            });
        });

    }

    processFilterChanges = (filterParameters: FilterColumnParameters) => {
        let requestFilters = this.tableFilterService.processFilterChanges(filterParameters);
        this.nmsTableComponent.filter(requestFilters);
    };

    ngOnDestroy(): void {
        this.nmsTableChangedSubscription.unsubscribe();
        this.nmsTableRulesRemovedSubscription.unsubscribe();
    }

    addLink() {
        if (this.linksShortcutActionService.userHasPermission(this.nmsFeatures.linkConfiguration.feature)) {
            this.nmsTableComponent.clearAllSelectedValues.next();
            this.modalFactory.openAsyncModal(AddLinkModalComponent);
        }
    }

    handleBeforeEdit(multipleRows: boolean) {
        if (this.linksShortcutActionService.userHasPermission(this.nmsFeatures.linkConfiguration.feature)
            && this.isValidForEdit(multipleRows)) {
            const linksSelecteds: LinkInfo[] = this.nmsTableComponent.selection.selected;
            this.linkService.getLinkById(linksSelecteds[0].id).subscribe(link => {
                this.modalFactory.openAsyncModal(AddLinkModalComponent, { linkInfo: link });
            });
        }
    }

    handleBeforeRemove() {
        if (this.linksShortcutActionService.userHasPermission(this.nmsFeatures.removeLinkFromNms.feature)
            && this.isValidForRemove()) {
            this.nmsTableValidatorService.showConfirmOptions(
                    this.linksTableService.getMessageForConfirmOptions(this.getRowsSelected()))
                .then(() => {
                    const linksSelecteds: LinkInfo[] = this.nmsTableComponent.selection.selected;
                    this.linkService.removeLinks(linksSelecteds);
                });
        }
    }

    notifyTableUpdateData({ data }: { data: PaginatedResponse }) {
        this.nmsTableComponent.updateData(data);
    }

    private setColumns() {
        this.columns = this.linksTableService.getColumns(this.isOpenInModal)
    }

    /**
     * Exibe mensagem de sucesso ao limpar todos os filtros/ordenações.
     */
    private subscribeTableRulesRemovedEvent() {
        this.nmsTableRulesRemovedSubscription = this.nmsTableComponent?.tableRulesRemovedEvent
            .subscribe((ruleType: TableDataRuleType) => {
                let message = this.translate.instant("nms.table.view.rule.sort.remove.all");

                if (ruleType === "filter") {
                    message = this.translate.instant("nms.table.view.rule.filtering.remove.all");
                }

                this.nmsToastrLinkService.showToastSuccess(message, []);
            });
    }

    /**
     * Verifica se atende as condições para edição de link:
     *  - verifica se possui linha selecionada;
     *  - verifica se é permitido selecionar múltiplas linhas.
     * @param multipleRows: booleano no qual indica se é permitido selecionar múltiplas linhas
     * @returns boolean
     */
    private isValidForEdit(multipleRows: boolean): boolean {
        const rowsSelectedModel = this.buildRowsValidationModel(this.translate.instant("table.links.no.item.selected"));
        const multipleRowsModel = this.buildRowsValidationModel(
            this.translate.instant("table.links.select.one.item"),
            multipleRows
        );

        return (
            this.nmsTableValidatorService.hasRowSelected(rowsSelectedModel) &&
            this.nmsTableValidatorService.allowMultipleRowsSelected(multipleRowsModel)
        );
    }

    /**
     * Verifica se atende a condição para remover um link.
     * Neste caso, verifica se possui ao menos uma linha selecionada.
     */
    private isValidForRemove(): boolean {
        const rowsSelectedModel = this.buildRowsValidationModel(this.translate.instant("table.links.no.item.selected"));

        return this.nmsTableValidatorService.hasRowSelected(rowsSelectedModel);
    }

    private buildRowsValidationModel(message: string, multipleRows?: boolean): RowsValidateModel {
        return {
            rowsSelected: this.getRowsSelected(),
            message: message,
            allowMultipleSelection: multipleRows || undefined
        };
    }

    private getRowsSelected(): number {
        return this.nmsTableComponent.selection.selected.length;
    }

    /**
     * Método responsável por atualizar os dados da tabela após uma operação (adição, edição ou remoção) ser com sucesso.
     * O método busca os dados atualizado no backend e envia os novos dados para o nms-table.
     */
    private updateTableDataAndNotify() {
        this.retrieveTableLinks().subscribe((paginatedResponse: PaginatedResponse) => {
            this.notifyTableUpdateData({ data: paginatedResponse });
        });
    }
}
